import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthShell, authPrimaryButtonClass } from '@/components/auth/AuthShell';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  useAuth,
  accountStatusFlags,
  AWAITING_SEAT_TITLE,
  AWAITING_SEAT_DESCRIPTION,
} from '@/contexts/AuthContext';

/**
 * Shown to an invitee who accepted while every seat on their team's plan was
 * in use (#1399). Their profile waits, inactive, until a seat frees up; Check
 * again asks the database to take one (claim_my_team_seat, under the per-org
 * seat lock) and opens the app when it can.
 */
export function AwaitingSeat() {
  const { signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<'check' | 'signout' | null>(null);
  const [stillFull, setStillFull] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkAgain = async () => {
    setBusy('check');
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('claim_my_team_seat');
      if (rpcError) throw rpcError;
      const flags = accountStatusFlags(data);
      if (flags && !flags.awaitingSeat && !flags.deactivated) {
        refreshProfile();
        return;
      }
      setStillFull(true);
    } catch {
      setError('We could not check right now. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <AuthShell title={AWAITING_SEAT_TITLE} subtitle={AWAITING_SEAT_DESCRIPTION}>
      <div data-testid="awaiting-seat" className="space-y-4">
        {stillFull && !error && (
          <p role="status" className="text-sm text-muted-foreground">
            Every seat is still in use. Your admin can cancel a pending invitation, deactivate a team member, or upgrade the plan.
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <Button
          type="button"
          className={authPrimaryButtonClass}
          disabled={busy !== null}
          onClick={() => void checkAgain()}
        >
          {busy === 'check' ? 'Checking…' : 'Check again'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={busy !== null}
          onClick={async () => {
            setBusy('signout');
            await signOut();
            navigate('/auth', { replace: true });
          }}
        >
          Sign out
        </Button>
      </div>
    </AuthShell>
  );
}
